import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Card, Tag, Tooltip, Button } from 'antd';
import { BrainCircuit, Zap, Settings } from 'lucide-react';
import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';

interface LLMNodeData {
  label: string;
  model: string;
  description?: string;
  config?: {
    temperature?: number;
    maxTokens?: number;
    systemPrompt?: string;
  };
  isExecuting?: boolean;
  isCompleted?: boolean;
  onDelete?: () => void;
  isUnlinked?: boolean;
}

const LLMNode: React.FC<NodeProps<LLMNodeData>> = ({ data, selected }) => {
  const { label, model, description, config, isExecuting, isCompleted, onDelete, isUnlinked } = data;

  const getCardStyle = () => {
    if (isExecuting) {
      return {
        borderColor: '#1890ff',
        boxShadow: '0 0 10px rgba(24, 144, 255, 0.5)',
        animation: 'pulse 1.5s ease-in-out infinite'
      };
    }
    if (isCompleted) {
      return {
        borderColor: '#52c41a',
        backgroundColor: '#f6ffed'
      };
    }
    if (selected) {
      return {
        borderColor: '#722ed1',
        boxShadow: '0 0 0 2px rgba(114, 46, 209, 0.2)'
      };
    }
    return {
      borderColor: '#d9d9d9'
    };
  };

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: '#1890ff' }}
      />
      
      <Card
        size="small"
        style={{
          width: 220,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          position: 'relative',
          ...getCardStyle()
        }}
        className="llm-node"
      >
        {/* Caution Icon for Unlinked Nodes */}
        {isUnlinked && (
          <Tooltip title="This node is not connected to any other nodes">
            <WarningOutlined
              style={{
                position: 'absolute',
                top: '4px',
                left: '4px',
                color: '#faad14',
                fontSize: '14px',
                zIndex: 10,
                backgroundColor: 'white',
                borderRadius: '50%',
                padding: '2px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}
            />
          </Tooltip>
        )}
        
        {/* Delete Button */}
        {onDelete && (
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              width: '20px',
              height: '20px',
              fontSize: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              opacity: 0.7,
              transition: 'opacity 0.2s'
            }}
            className="node-delete-btn"
          />
        )}
        <div className="flex items-start space-x-2">
          <div className="flex-shrink-0">
            <div className={`p-2 rounded-lg ${isExecuting ? 'bg-blue-100' : isCompleted ? 'bg-green-100' : 'bg-purple-100'}`}>
              {isExecuting ? (
                <Zap className="h-4 w-4 text-blue-600" />
              ) : (
                <BrainCircuit className={`h-4 w-4 ${isCompleted ? 'text-green-600' : 'text-purple-600'}`} />
              )}
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-900 truncate">
                {label}
              </h4>
              {config && (
                <Tooltip title="Node Configuration">
                  <Settings className="h-3 w-3 text-gray-400" />
                </Tooltip>
              )}
            </div>
            
            <div className="mt-1">
              <Tag color="purple">
                {model}
              </Tag>
            </div>
            
            {description && (
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                {description}
              </p>
            )}
            
            {config && (
              <div className="mt-2 text-xs text-gray-600">
                <div className="flex justify-between">
                  <span>Temp: {config.temperature || 0.7}</span>
                  <span>Tokens: {config.maxTokens || 1000}</span>
                </div>
              </div>
            )}
            
            {isExecuting && (
              <div className="mt-2">
                <div className="flex items-center space-x-1">
                  <div className="animate-spin h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full" />
                  <span className="text-xs text-blue-600">Processing...</span>
                </div>
              </div>
            )}
            
            {isCompleted && (
              <div className="mt-2">
                <div className="flex items-center space-x-1">
                  <div className="h-3 w-3 bg-green-600 rounded-full" />
                  <span className="text-xs text-green-600">Completed</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
      
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: '#1890ff' }}
      />
      
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        .llm-node:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }

        .llm-node .node-delete-btn {
          opacity: 0;
          transition: opacity 0.2s;
        }

        .llm-node:hover .node-delete-btn {
          opacity: 1;
        }

        .node-delete-btn:hover {
          background-color: #ff4d4f !important;
          color: white !important;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default LLMNode;